package com.schoolconnect.app.feature.examresults

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.designsystem.list.AdminListSearchField
import com.schoolconnect.app.designsystem.list.AdminListSummaryCard
import com.schoolconnect.app.designsystem.list.AdminListTableBody
import com.schoolconnect.app.designsystem.list.AdminListTableCard
import com.schoolconnect.app.designsystem.list.AdminListFooter
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.pageFooterText
import com.schoolconnect.app.feature.common.pageWindow
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chart_square
import ischoolconnect.sharedui.generated.resources.clipboard_check
import ischoolconnect.sharedui.generated.resources.clock
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.message
import ischoolconnect.sharedui.generated.resources.pencil
import ischoolconnect.sharedui.generated.resources.school_users
import ischoolconnect.sharedui.generated.resources.users
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import kotlin.math.PI

private enum class ResultDialogTab { Summary, EnterMarks }

@Composable
internal fun ExamResultDialog(
    row: ExamResultRowUi?,
    initialEnterMarks: Boolean,
    onDismiss: () -> Unit
) {
    row ?: return
    val details = remember(row.id) { dummyExamResultDetails(row) }
    var selectedTab by remember(row.id, initialEnterMarks) {
        mutableStateOf(if (initialEnterMarks) ResultDialogTab.EnterMarks else ResultDialogTab.Summary)
    }

    AppDialogScaffold(onDismiss = onDismiss, size = AppDialogSize.Wide, maxDialogHeight = 1000.dp) {
        AppDialogHeader(
            title = if (selectedTab == ResultDialogTab.Summary) "Exam Results" else "Enter Marks",
            subtitle = "",
            icon = if (selectedTab == ResultDialogTab.Summary) Res.drawable.clipboard_check else Res.drawable.pencil,
            onClose = onDismiss,
            colors = AppDialogDefaultColors
        )
        ResultHeaderInfo(row)
        ResultDialogTabs(selectedTab) { selectedTab = it }
        AppDialogDivider()
        Column(Modifier.fillMaxWidth().weight(1f).verticalScroll(rememberScrollState()).padding(18.dp)) {
            if (selectedTab == ResultDialogTab.Summary) ResultSummaryContent(row, details) else EnterMarksContent(row, details)
        }
        AppDialogDivider()
        ResultDialogFooter(selectedTab, onDismiss)
    }
}

@Composable
private fun ResultHeaderInfo(row: ExamResultRowUi) {
    Row(Modifier.fillMaxWidth().padding(horizontal = 28.dp, vertical = 2.dp), horizontalArrangement = Arrangement.spacedBy(24.dp)) {
        ResultInfo("Exam ID", row.examId, Modifier.weight(1f))
        ResultInfo("Exam Name", row.exam, Modifier.weight(1.35f))
        ResultInfo("Class", row.className, Modifier.weight(.75f))
        ResultInfo("Exam Date", row.date, Modifier.weight(1f))
        ResultInfo("Students", "42", Modifier.weight(.75f))
        ResultInfo("Status", row.status, Modifier.weight(.9f), statusColor(row.status))
    }
}

@Composable
private fun ResultInfo(label: String, value: String, modifier: Modifier, valueColor: Color = AppNavy) {
    Column(modifier.padding(vertical = 8.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(label, color = AppMuted, fontSize = 12.sp, fontWeight = FontWeight.Medium)
        Text(value, color = valueColor, fontSize = 14.sp, fontWeight = FontWeight.Bold, maxLines = 1)
    }
}

@Composable
private fun ResultDialogTabs(selected: ResultDialogTab, onSelected: (ResultDialogTab) -> Unit) {
    Row(Modifier.fillMaxWidth().padding(horizontal = 28.dp, vertical = 12.dp), horizontalArrangement = Arrangement.spacedBy(2.dp)) {
        ResultTab("1", "Summary", selected == ResultDialogTab.Summary) { onSelected(ResultDialogTab.Summary) }
        ResultTab("2", "Enter Marks", selected == ResultDialogTab.EnterMarks) { onSelected(ResultDialogTab.EnterMarks) }
    }
}

@Composable
private fun ResultTab(number: String, label: String, selected: Boolean, onClick: () -> Unit) {
    Row(
        Modifier
            .height(48.dp)
            .background(if (selected) AppPrimary else AppSoftSurface, RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp))
            .clickable(onClick = onClick)
            .padding(horizontal = 20.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Box(Modifier.size(24.dp).border(1.dp, if (selected) Color.White else AppBorder, RoundedCornerShape(5.dp)), contentAlignment = Alignment.Center) {
            Text(number, color = if (selected) Color.White else AppNavy, fontSize = 12.sp, fontWeight = FontWeight.Bold)
        }
        Text(label, color = if (selected) Color.White else AppNavy, fontWeight = FontWeight.Bold, fontSize = 14.sp)
    }
}

@Composable
private fun ResultSummaryContent(row: ExamResultRowUi, details: ExamResultDetailsUi) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.Top) {
            Row(Modifier.weight(2.35f), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                ResultMetric("Total Students", details.totalStudents.toString(), "All Students", Res.drawable.users, AppPrimary, AppPrimarySoft, Modifier.weight(1f))
                ResultMetric("Completed", details.completedStudents.toString(), "${details.completedStudents * 100 / details.totalStudents}% Completed", Res.drawable.clipboard_check, AppSuccess, AppSuccessSoft, Modifier.weight(1f))
                ResultMetric("Pending", details.pendingStudents.toString(), "${details.pendingStudents * 100 / details.totalStudents}% Pending", Res.drawable.clock, AppOrangeText, AppOrangeSoft, Modifier.weight(1f))
                ResultMetric("Published Status", row.status, if (row.status == "Published") "Published" else "Not Published Yet", Res.drawable.clipboard_check, AppPurpleText, AppPurpleSoft, Modifier.weight(1f))
            }
            ClassStatisticsCard(details, Modifier.weight(1.15f))
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            GradeDistributionCard(details, Modifier.weight(1.05f))
            SubjectCompletionCard(details, Modifier.weight(1.15f))
            AttentionStudentsCard(details, Modifier.weight(1.15f))
        }
    }
}

@Composable
private fun ClassStatisticsCard(details: ExamResultDetailsUi, modifier: Modifier) {
    ResultSectionCard("Class Statistics", Res.drawable.chart_square, modifier) {
        val stats = listOf(
            "Highest Marks" to details.highestMarks, "Lowest Marks" to details.lowestMarks, "Class Average" to details.classAverage,
            "Highest %" to details.highestPercentage, "Average %" to details.averagePercentage, "Pass Percentage" to details.passPercentage
        )
        Column(Modifier.fillMaxWidth()) {
            Row(Modifier.fillMaxWidth()) { stats.take(3).forEach { (label, value) -> ResultStatCell(label, value, Modifier.weight(1f)) } }
            HorizontalDivider(color = AppBorder)
            Row(Modifier.fillMaxWidth()) { stats.drop(3).forEach { (label, value) -> ResultStatCell(label, value, Modifier.weight(1f), valueColor = if (label == "Pass Percentage") AppSuccess else AppPrimary) } }
        }
    }
}

@Composable
private fun ResultStatCell(label: String, value: String, modifier: Modifier, valueColor: Color = AppPrimary) {
    Column(modifier.padding(horizontal = 10.dp, vertical = 14.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(label, color = AppMuted, fontSize = 11.sp, fontWeight = FontWeight.Medium)
        Text(value, color = valueColor, fontSize = 13.sp, fontWeight = FontWeight.Bold)
    }
}

@Composable
private fun ResultMetric(title: String, value: String, note: String, icon: DrawableResource, accent: Color, iconBackground: Color, modifier: Modifier) {
    AdminListSummaryCard(
        title = title,
        value = value,
        subtitle = note,
        icon = painterResource(icon),
        accent = accent,
        accentBg = iconBackground,
        subtitleColor = accent,
        modifier = modifier,
        surfaceColor = Color.White,
        borderColor = AppBorder,
        textColor = AppNavy,
        contentPadding = PaddingValues(horizontal = 14.dp, vertical = 14.dp),
        iconSize = 54.dp,
        iconGlyphSize = 28.dp,
        contentSpacing = 12.dp,
        titleFontSize = 12.sp,
        valueFontSize = 26.sp,
        subtitleFontSize = 11.sp
    )
}

@Composable
private fun ResultSectionCard(title: String, icon: DrawableResource, modifier: Modifier, content: @Composable ColumnScope.() -> Unit) {
    AppCard(modifier, containerColor = Color.White) {
        Row(Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 14.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            Icon(painterResource(icon), null, tint = AppPrimary, modifier = Modifier.size(22.dp))
            Text(title, color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 15.sp)
        }
        HorizontalDivider(color = AppBorder)
        Column(Modifier.padding(16.dp), content = content)
    }
}

@Composable
private fun GradeDistributionCard(details: ExamResultDetailsUi, modifier: Modifier) {
    ResultSectionCard("Grade Distribution", Res.drawable.chart_square, modifier) {
        val grades = details.gradeDistribution
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.CenterVertically) {
            Canvas(Modifier.size(150.dp)) {
                val colors = listOf(AppPrimary, AppSuccess, Color(0xFFFFA600), AppPurpleText, AppDanger, Color(0xFF9CA3AF))
                val total = grades.sumOf { it.students }.toFloat()
                var start = -90f
                grades.forEachIndexed { index, grade ->
                    val sweep = grade.students / total * 360f
                    drawArc(colors[index], start, sweep, true)
                    start += sweep
                }
                drawCircle(Color.White, radius = size.minDimension * .23f)
            }
            Column(Modifier.weight(1f)) {
                Row(Modifier.fillMaxWidth().background(AppSoftSurface).padding(8.dp)) {
                    Text("Grade", Modifier.weight(1f), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                    Text("Students", Modifier.weight(1f), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                    Text("%", Modifier.weight(.7f), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                }
                val total = grades.sumOf { it.students }.toFloat()
                grades.forEachIndexed { index, grade ->
                    Row(Modifier.fillMaxWidth().padding(vertical = 5.dp, horizontal = 8.dp)) {
                        Text(grade.grade, Modifier.weight(1f), color = listOf(AppPrimary, AppSuccess, Color(0xFFFFA600), AppPurpleText, AppDanger, AppMuted)[index], fontWeight = FontWeight.Bold, fontSize = 11.sp)
                        Text(grade.students.toString(), Modifier.weight(1f), color = AppNavy, fontSize = 11.sp)
                        Text("${(grade.students / total * 100).toInt()}%", Modifier.weight(.7f), color = AppNavy, fontSize = 11.sp)
                    }
                }
                HorizontalDivider(color = AppBorder)
                Row(Modifier.fillMaxWidth().padding(vertical = 6.dp, horizontal = 8.dp)) {
                    Text("Total", Modifier.weight(1f), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                    Text(grades.sumOf { it.students }.toString(), Modifier.weight(1f), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                    Text("100%", Modifier.weight(.7f), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                }
            }
        }
    }
}

@Composable
private fun SubjectCompletionCard(details: ExamResultDetailsUi, modifier: Modifier) {
    ResultSectionCard("Subject Completion Overview", Res.drawable.clipboard_check, modifier) {
        Row(Modifier.fillMaxWidth().background(AppSoftSurface).padding(vertical = 8.dp, horizontal = 8.dp)) {
            Text("Subject", Modifier.weight(1.25f), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
            Text("Completed", Modifier.weight(.8f), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
            Text("Pending", Modifier.weight(.65f), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
            Text("Completion %", Modifier.weight(1.1f), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
        }
        details.subjectCompletion.forEach { completion ->
            Row(Modifier.fillMaxWidth().padding(vertical = 7.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(completion.subject, Modifier.weight(1.25f), color = AppNavy, fontSize = 12.sp)
                Text(completion.completed.toString(), Modifier.weight(.8f), color = AppSuccess, fontWeight = FontWeight.Bold, fontSize = 12.sp)
                Text(completion.pending.toString(), Modifier.weight(.65f), color = AppDanger, fontSize = 12.sp)
                Text(completion.completionPercentage, Modifier.weight(.45f), color = AppNavy, fontSize = 11.sp)
                Box(Modifier.weight(1f).height(7.dp).background(AppSoftSurface, RoundedCornerShape(8.dp))) { Box(Modifier.fillMaxWidth((completion.completed.toFloat() / details.totalStudents).coerceIn(0f, 1f)).fillMaxHeight().background(AppSuccess, RoundedCornerShape(8.dp))) }
            }
        }
    }
}

@Composable
private fun AttentionStudentsCard(details: ExamResultDetailsUi, modifier: Modifier) {
    ResultSectionCard("Bottom 5 Students (Needs Attention)", Res.drawable.info_circle, modifier) {
        Row(Modifier.fillMaxWidth().background(AppDangerSoft).padding(vertical = 8.dp, horizontal = 8.dp)) {
            Text("Rank", Modifier.width(38.dp), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
            Text("Student Name", Modifier.weight(1f), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
            Text("Total Marks", Modifier.width(86.dp), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp, maxLines = 1)
            Text("Percentage", Modifier.width(88.dp), color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp, maxLines = 1)
        }
        details.students.filter { it.totalMarks != null }.sortedBy { it.totalMarks }.take(5).forEachIndexed { index, student ->
            Row(Modifier.fillMaxWidth().padding(vertical = 7.dp), horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                Text("${index + 1}", Modifier.width(38.dp), color = AppDanger, fontWeight = FontWeight.Bold, fontSize = 12.sp)
                Text(student.name, Modifier.weight(1f), color = AppNavy, fontSize = 12.sp)
                Text("${student.totalMarks} / 500", Modifier.width(86.dp), color = AppNavy, fontSize = 11.sp, maxLines = 1)
                Text(student.totalPercentage, Modifier.width(88.dp), color = AppNavy, fontSize = 11.sp, maxLines = 1)
            }
        }
    }
}

@Composable
private fun EnterMarksContent(row: ExamResultRowUi, details: ExamResultDetailsUi) {
    var studentSearch by remember(row.id) { mutableStateOf("") }
    var studentsPageIndex by remember(row.id) { mutableStateOf(0) }
    var studentsPageSize by remember(row.id) { mutableStateOf(PaginationPolicy.DEFAULT_PAGE_SIZE) }
    var studentsRowsMenuExpanded by remember(row.id) { mutableStateOf(false) }

    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
            AdminListSearchField(
                value = studentSearch,
                onValueChange = { studentSearch = it },
                placeholder = "Search student by name or roll no.",
                width = 370.dp,
                height = 44.dp,
                surfaceColor = Color.White,
                borderColor = AppBorder,
                iconTint = AppMuted,
                textColor = AppNavy,
                cursorColor = AppPrimary
            )
            Text("I - Internal    M - Main    T - Total (Auto Calculated)", color = AppMuted, fontSize = 12.sp)
        }
        val subjects = listOf("English", "Mathematics", "Science", "Social Science", "Hindi")
        val students = details.students.filter { student ->
            val query = studentSearch.trim()
            query.isBlank() || student.name.contains(query, ignoreCase = true) || student.rollNo.contains(query, ignoreCase = true)
        }
        val studentsWindow = pageWindow(studentsPageIndex, studentsPageSize, students.size)
        val pageStudents = students.drop((studentsWindow.startIndex - 1).coerceAtLeast(0)).take(studentsWindow.pageSize)
        val rollWidth = 72.dp
        val studentWidth = 170.dp
        val summaryWidth = 84.dp
        val subjectWidth = 220.dp
        val subjectMarkWidth = 73.dp
        val subjectHeaderColors = listOf(
            AppPrimarySoft,
            AppSuccessSoft,
            AppPurpleSoft,
            AppOrangeSoft,
            Color(0xFFE4F7FA)
        )
        AdminListTableCard(
            modifier = Modifier.fillMaxWidth().height(520.dp),
            minContentWidth = 1762.dp,
            header = {
                Column(Modifier.fillMaxWidth().background(AppSoftSurface)) {
                    Row(Modifier.fillMaxWidth().height(36.dp).padding(horizontal = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                        Spacer(Modifier.width(rollWidth + studentWidth))
                        TableGroupHeader("Results Summary", Modifier.width(summaryWidth * 5))
                        TableGroupHeader("Subject Marks", Modifier.width(subjectWidth * subjects.size))
                    }
                    Row(Modifier.fillMaxWidth().height(42.dp).padding(horizontal = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                        TableHeaderCell("Roll No.", rollWidth)
                        TableHeaderCell("Student Name", studentWidth)
                        listOf("Total\n/ 500", "Total %", "Grade", "Rank", "Notes").forEach { label ->
                            TableHeaderCell(label, summaryWidth)
                        }
                        subjects.forEachIndexed { index, subject ->
                            TableHeaderCell(subject, subjectWidth, subjectHeaderColors[index])
                        }
                    }
                    Row(Modifier.fillMaxWidth().height(48.dp).padding(horizontal = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                        Spacer(Modifier.width(rollWidth + studentWidth + summaryWidth * 5))
                        subjects.forEachIndexed { index, _ ->
                            listOf("I\n20", "M\n80", "T\n100").forEach { label ->
                                TableHeaderCell(label, subjectMarkWidth, subjectHeaderColors[index])
                            }
                        }
                    }
                }
            },
            body = {
                AdminListTableBody(items = pageStudents, key = { it.rollNo }, modifier = Modifier.fillMaxWidth()) { student ->
                    val index = students.indexOf(student)
                    val hasMarks = row.status != "Not Started"
                    Row(Modifier.fillMaxWidth().height(58.dp).padding(horizontal = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                        TableBodyCell(student.rollNo, rollWidth)
                        TableBodyCell(student.name, studentWidth, FontWeight.SemiBold, Alignment.CenterStart)
                        TableBodyCell(if (hasMarks) student.totalMarks?.toString() ?: "–" else "–", summaryWidth)
                        TableBodyCell(if (hasMarks) student.totalPercentage else "–", summaryWidth)
                        GradeCell(if (hasMarks) student.grade else "–", summaryWidth)
                        TableBodyCell(if (hasMarks) student.rank.ifBlank { "–" } else "–", summaryWidth)
                        Box(Modifier.width(summaryWidth), contentAlignment = Alignment.Center) {
                            Icon(painterResource(Res.drawable.message), "Notes", tint = AppNavy, modifier = Modifier.size(22.dp))
                        }
                        student.subjects.forEach { subject ->
                            Row(Modifier.width(subjectWidth), verticalAlignment = Alignment.CenterVertically) {
                                MarksInputField(value = if (hasMarks) subject.internal?.toString().orEmpty() else "", modifier = Modifier.width(subjectMarkWidth).padding(horizontal = 7.dp))
                                MarksInputField(value = if (hasMarks) subject.main?.toString().orEmpty() else "", modifier = Modifier.width(subjectMarkWidth).padding(horizontal = 7.dp))
                                TableBodyCell(if (hasMarks) subject.total?.toString() ?: "–" else "–", subjectMarkWidth)
                            }
                        }
                    }
                    if (student != pageStudents.last()) HorizontalDivider(color = AppBorder)
                }
            },
            footer = {
                AdminListFooter(
                    pageText = pageFooterText(studentsWindow, "students"),
                    pageIndex = studentsWindow.pageIndex,
                    pageCount = studentsWindow.pageCount,
                    onPageChanged = { studentsPageIndex = it },
                    pageSize = studentsPageSize,
                    rowsPerPageExpanded = studentsRowsMenuExpanded,
                    onRowsPerPageToggle = { studentsRowsMenuExpanded = !studentsRowsMenuExpanded },
                    onRowsPerPageDismiss = { studentsRowsMenuExpanded = false },
                    onPageSizeChanged = {
                        studentsPageSize = it
                        studentsPageIndex = 0
                        studentsRowsMenuExpanded = false
                    }
                )
            }
        )
    }
}

@Composable
private fun TableGroupHeader(text: String, modifier: Modifier) {
    Box(modifier, contentAlignment = Alignment.Center) {
        Text(text, color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 12.sp)
    }
}

@Composable
private fun TableHeaderCell(text: String, width: Dp, backgroundColor: Color = Color.Transparent) {
    Box(Modifier.width(width).fillMaxHeight().background(backgroundColor), contentAlignment = Alignment.Center) {
        Text(text, color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp, textAlign = androidx.compose.ui.text.style.TextAlign.Center)
    }
}

@Composable
private fun TableBodyCell(
    text: String,
    width: Dp,
    fontWeight: FontWeight = FontWeight.Normal,
    alignment: Alignment = Alignment.Center
) {
    Box(Modifier.width(width), contentAlignment = alignment) {
        Text(text, color = AppNavy, fontWeight = fontWeight, fontSize = 12.sp, maxLines = 1)
    }
}

@Composable
private fun GradeCell(grade: String, width: Dp) {
    Box(
        Modifier.width(width).padding(horizontal = 10.dp).height(34.dp)
            .border(1.dp, AppBorder, RoundedCornerShape(7.dp)),
        contentAlignment = Alignment.Center
    ) {
        Row(Modifier.fillMaxWidth().padding(horizontal = 8.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
            Text(grade, color = AppNavy, fontSize = 12.sp, maxLines = 1)
            Icon(painterResource(Res.drawable.chevron_down), null, Modifier.size(14.dp), tint = AppNavy)
        }
    }
}

@Composable
private fun MarksInputField(value: String, modifier: Modifier) {
    var editedValue by remember(value) { mutableStateOf(value) }

    AppDialogTextField(
        value = editedValue,
        onValueChange = { editedValue = it.filter(Char::isDigit).take(3) },
        placeholder = "",
        modifier = modifier,
        colors = AppDialogDefaultColors,
        keyboardType = KeyboardType.Number,
        fieldHeight = 34.dp,
        fontSize = 12.sp
    )
}

@Composable
private fun ResultDialogFooter(selectedTab: ResultDialogTab, onDismiss: () -> Unit) {
    AppDialogFooter(
        leadingContent = {
            Row(Modifier.background(AppInfoBg, RoundedCornerShape(8.dp)).padding(horizontal = 14.dp, vertical = 10.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Icon(painterResource(Res.drawable.info_circle), null, tint = AppPrimary, modifier = Modifier.size(18.dp))
                Text(if (selectedTab == ResultDialogTab.Summary) "Review the summary before publishing the results." else "Enter internal and main marks for each subject. Totals are calculated automatically.", color = AppNavy, fontSize = 12.sp)
            }
        },
        actions = listOf(
            AppDialogFooterAction("Close", onDismiss, width = 120.dp),
            AppDialogFooterAction("Save Draft", {}, width = 140.dp),
            AppDialogFooterAction("Save & Publish", {}, AppDialogActionStyle.Primary, width = 160.dp)
        ),
        colors = AppDialogDefaultColors,
        footerHeight = 92.dp
    )
}

private fun statusColor(status: String) = when (status) { "Published" -> AppSuccess; "Draft" -> AppOrangeText; else -> AppNavy }
